import { post, get, clearSession, currentSession, saveSession, type Session, type SessionUser } from './client'

/**
 * Surface d'authentification. Le module `client` sait *stocker* une session ;
 * c'est ici qu'on sait comment on l'*obtient*. Cette séparation permet de
 * changer de fournisseur d'identité (OIDC, SSO) sans toucher au client HTTP.
 */

export interface LoginPayload {
  email: string
  password: string
}


/**
 * L'AUTRE porte : une preuve d'accès beTool contre une session de ce service.
 *
 * Même contrat de sortie que `login` — même `Session`, même stockage — et c'est
 * l'essentiel : betool-sign reste l'arbitre de la durée, du rôle et de la
 * portée d'une session chez lui. L'identité dit QUI ; elle ne dit jamais ce
 * qu'on peut faire.
 *
 * La preuve voyage dans l'en-tête, jamais dans le corps : un corps JSON
 * finirait dans un proxy ou un outil de rejeu qui ne masque que les en-têtes,
 * et la capacité serait archivée en clair.
 */
export async function entrerParPreuve(preuve: string, identite?: string): Promise<Session> {
  const ouverte = await post<Session>('/betool/session', undefined, {
    headers: { authorization: `Bearer ${preuve}` },
  })
  // L'identité de l'entrée voyage AVEC la session : c'est elle que l'onglet
  // incarne (voir `Session.identiteBetool`).
  const session: Session = identite ? { ...ouverte, identiteBetool: identite } : ouverte
  saveSession(session)
  return session
}

/**
 * Déconnexion. Le jeton de rafraîchissement est révoqué côté serveur, mais la
 * session locale est effacée QUOI QU'IL ARRIVE : si l'appel échoue (hors ligne,
 * API redémarrée), l'utilisateur doit tout de même se retrouver déconnecté sur
 * ce poste. L'inverse — rester connecté parce que le réseau est tombé — serait
 * un défaut de sécurité.
 */
export async function logout(): Promise<void> {
  const refreshToken = currentSession()?.refreshToken
  try {
    if (refreshToken) await post<void>('/auth/logout', { refreshToken })
  } catch {
    // Silence volontaire : voir ci-dessus.
  } finally {
    clearSession()
  }
}

/** Récupère l'utilisateur courant depuis le serveur (vérifie aussi le jeton). */
export function me(): Promise<SessionUser> {
  return get<SessionUser>('/auth/me')
}

/**
 * Revient chez soi après un « jump » backoffice (E15-1). Idempotent côté
 * serveur : sans jump en cours, la route rend simplement le profil courant.
 *
 * Ici et non dans `superuser.ts` : la route n'est PAS sous la garde du
 * périmètre plateforme — pendant le jump, l'organisation effective est celle du
 * client et cette garde refuserait l'agent. C'est un acte de SESSION.
 */
export function quitterJump(): Promise<SessionUser> {
  return post<SessionUser>('/auth/jump/return')
}


